import { useParams, Link } from 'react-router-dom';
import { articles, Article } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ArticleCard from '@/components/feature/ArticleCard';
import CategoryTabs from '@/components/feature/CategoryTabs';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import PageMeta from '@/components/feature/PageMeta';
import { WebPageSchema } from '@/components/feature/SchemaOrg';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="w-full px-4 md:px-8 py-20 text-center">
          <h1 className="text-2xl font-bold font-heading text-foreground-950 mb-4">分类不存在</h1>
          <Link to="/" className="text-accent-500 hover:text-accent-600 font-medium">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  const categoryArticles: Article[] = articles.filter((a) => a.category === slug);

  return (
    <>
      <ScrollToTop />
      <PageMeta
        title={`${category.name} - 黑料bu｜${category.description}`}
        description={category.description}
        keywords={`黑料bu,${category.name},黑料bu${category.name},黑料`}
        canonicalUrl={`https://heiliao.bu/category/${slug}`}
      />
      <WebPageSchema
        name={`黑料bu - ${category.name}`}
        url={`https://heiliao.bu/category/${slug}`}
        description={category.description}
        dateModified="2026-08-07"
      />

      <Navbar />
      <Breadcrumb items={[
        { label: '首页', href: '/' },
        { label: category.name },
      ]} />

      <main>
        <section className="w-full px-4 md:px-8 py-8 md:py-12">
          <div className="mb-6">
            <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 mb-2">
              {category.name}
            </h1>
            <p className="text-sm text-foreground-600">{category.description}</p>
          </div>

          <CategoryTabs activeSlug={slug} />
        </section>

        <section className="w-full px-4 md:px-8 pb-12 md:pb-16">
          {categoryArticles.length > 0 ? (
            <>
              {categoryArticles.length >= 2 && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-8">
                  {categoryArticles.slice(0, 2).map((article) => (
                    <Link
                      key={article.id}
                      to={`/article/${article.id}`}
                      className="group bg-background-50 border border-background-200 rounded-lg overflow-hidden hover:border-accent-300 transition-all duration-300"
                    >
                      <div className="flex flex-col sm:flex-row">
                        <div className="w-full sm:w-56 h-48 sm:h-auto flex-shrink-0 overflow-hidden">
                          <img
                            src={article.imageUrl}
                            alt={article.title}
                            title={`${article.title} - 黑料bu`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                        <div className="p-5 flex flex-col justify-center">
                          {article.isHot && (
                            <span className="inline-block bg-accent-500 text-background-50 text-xs font-semibold px-2 py-0.5 rounded-md mb-2 w-fit">
                              热门
                            </span>
                          )}
                          <h3 className="text-lg font-semibold font-heading text-foreground-950 group-hover:text-accent-500 transition-colors mb-2 line-clamp-2">
                            {article.title}
                          </h3>
                          <p className="text-sm text-foreground-600 line-clamp-2 mb-3">{article.excerpt}</p>
                          <div className="flex items-center gap-4 text-xs text-foreground-500">
                            <span>{article.date}</span>
                            <span>{article.views > 10000 ? `${(article.views / 10000).toFixed(1)}万阅读` : `${article.views}阅读`}</span>
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
                {categoryArticles.slice(2).map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            </>
          ) : (
            <div className="text-center py-16">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
                <i className="ri-inbox-line text-2xl text-foreground-400"></i>
              </div>
              <p className="text-foreground-600">该分类暂无文章</p>
              <Link to="/" className="text-accent-500 hover:text-accent-600 text-sm font-medium mt-2 inline-block">
                返回首页浏览更多
              </Link>
            </div>
          )}
        </section>
      </main>

      <Footer />
    </>
  );
}